/**
 * Fase 2 — bloque "FACTURA ESTA COMPRA" del ticket impreso (térmico 80/58 mm):
 * QR + código del ticket + URL corta + RFC/razón del EMISOR. Se auto-oculta si
 * el emisor del tenant no está listo (ready:false) o si el fetch falla — el
 * ticket sale igual que siempre. Lo imprime el cajero (pos:view): el endpoint
 * ticket-info no exige permisos cfdi_sales.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../lib/api';

interface TicketCfdiInfo {
  ready: boolean;
  issuerRfc: string | null;
  issuerRazon: string | null;
  code: string;
  /** NULL = regla SAT (hasta fin del mes de la venta); N = días desde el cierre. */
  windowDays: number | null;
  /** V113 — si el tenant emite factura global y con qué periodicidad. */
  globalEnabled?: boolean;
  globalPeriodicity?: string | null;
}

/** Leyenda de plazo del ticket. Con global activo, se pide facturar antes de que
 *  acabe el período (tras el cual la venta entra a la factura global). */
const periodLabel = (p?: string | null): string =>
  p === '01' ? 'del día' : p === '02' ? 'de la semana' : 'del mes';

/** 16 hex → "XXXX-XXXX-XXXX-XXXX" (legible para teclearlo a mano). */
const prettyCode = (code: string) => (code.match(/.{1,4}/g) ?? [code]).join('-').toUpperCase();

export function SelfInvoiceTicketBlock({ orderId }: { orderId: string }) {
  const [info, setInfo] = useState<TicketCfdiInfo | null>(null);
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api<TicketCfdiInfo>(`/cfdi-sales/ticket-info/${orderId}`)
      .then(async (r) => {
        if (!alive || !r.ready) return;
        const url = `${window.location.origin}/factura/${r.code}`;
        const dataUrl = await QRCode.toDataURL(url, { margin: 1, width: 132 });
        if (!alive) return;
        setInfo(r);
        setQr(dataUrl);
      })
      .catch(() => { /* emisor no listo o sin módulo: ticket sin bloque */ });
    return () => { alive = false; };
  }, [orderId]);

  if (!info || !qr) return null;

  const url = `${window.location.origin.replace(/^https?:\/\//, '')}/factura/${info.code}`;

  return (
    <div className="mt-4 border-t border-dashed border-slate-300 pt-3 text-center">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700">Factura esta compra</p>
      <img src={qr} alt="QR para autofacturar" className="mx-auto mt-1 h-[132px] w-[132px]" />
      <p className="font-mono text-[11px] text-slate-700">Ticket: {prettyCode(info.code)}</p>
      <p className="break-all font-mono text-[10px] text-slate-500">{url}</p>
      {info.globalEnabled && (
        <p className="mt-1 text-[10px] font-semibold text-slate-700">
          Realiza tu factura antes de que acabe el período {periodLabel(info.globalPeriodicity)}.
        </p>
      )}
      <p className="mt-1 text-[10px] text-slate-500">
        Emisor: {info.issuerRazon} · RFC {info.issuerRfc} ·{' '}
        {info.windowDays ? `dentro de ${info.windowDays} días` : 'hasta fin de mes'}
      </p>
    </div>
  );
}
